/**
 * SOLID Review
 * S — Strong. One use case: peek under the cup — the plan, the server's dice, the dice shown.
 * O — Strong. The round trip runs it; a change of rule is the Domain's, of sequence the round trip's.
 * L — Strong. Implements its use-case role and the round trip's three strategies exactly.
 * I — Strong. `execute`, and the three strategies the round trip asks for.
 * D — Strong. The decisions and server ports and the round trip, injected.
 */
import type { IContextRequest } from '../ports/IContextRequest';
import type { IPropsClientTableInput } from '../../domain/ports/schemas/ClientTable';
import type { ICommandNothing, ICommandPeeked, IPayloadRoundMove } from '../../domain/ports/schemas/TableMoves';
import type { TPortOutcome } from '../ports/PortResult';
import type { IUseCase } from '../uml-components/IUseCase';
import type { TServerRoundTrip, TAnswered } from '../shared/ServerRoundTrip';
import type { IConfigDepTable } from './IConfigDepTable';

/** The roller peeks (FE1's peekDice): the server answers the dice under the cup, and the table shows them. */
export class PeekDiceUseCase implements IUseCase<ICommandNothing, TPortOutcome>, TServerRoundTrip<IPropsClientTableInput, ICommandNothing, IPayloadRoundMove, TAnswered<ICommandPeeked['answer']>> {
  constructor(private readonly deps: IConfigDepTable) {}

  execute(ctx: IContextRequest, command: ICommandNothing): Promise<TPortOutcome> {
    return this.deps.trip.run(ctx, command, this);
  }

  plan(ctx: IContextRequest, input: IPropsClientTableInput, command: ICommandNothing) {
    return this.deps.decisions.planPeek(ctx, input, command);
  }

  send(ctx: IContextRequest, plan: IPayloadRoundMove) {
    return this.deps.rounds.peekDice(ctx, plan);
  }

  answer(ctx: IContextRequest, input: IPropsClientTableInput, plan: IPayloadRoundMove, answered: TAnswered<ICommandPeeked['answer']>) {
    const peeked: ICommandPeeked = { request: plan, answer: answered.value };
    return this.deps.decisions.peeked(ctx, input, peeked);
  }
}
